import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { formsApi, type ReviewView } from '@/api/formsApi'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { allFields } from '@/lib/formSections'
import { PERMISSIONS, useOptionalPermission } from '@/lib/permissions'
import { useOptionalTenant } from '@/providers/TenantProvider'
import { useInvalidatePendingReviews } from '@/hooks/usePendingReviews'
import { useAuditTrailInvalidator } from '@/hooks/useAuditTrail'
import { cn } from '@/lib/utils'
import type { FormDefinition, FormSubmission } from '@/types/cgt'

const UNASSIGNED = '__unassigned__'

const STATUS_CHIP: Record<ReviewView['status'], { label: string; className: string }> = {
  unassigned: { label: 'Unassigned', className: 'bg-gray-100 text-gray-600' },
  assigned: { label: 'Assigned', className: 'bg-blue-50 text-blue-700' },
  reviewed: { label: 'Reviewed', className: 'bg-green-50 text-green-700' },
}

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}

/**
 * The Review area (z8nrz7dt41): every Review field on the form, gathered into
 * one grey block at the bottom whichever section it was placed in.
 *
 * Each row is Assigned · Status · Electronic Signature. A reviewer can be
 * assigned from the moment the form is started, but the signature is only
 * available once the form is completed and locked — and only to the person
 * assigned. The "Assigned" list comes from the server with everyone who
 * performs the step already left out; the server re-checks every rule, so the
 * disabled states here are courtesy, not the control.
 */
export function ReviewArea({
  definition,
  submission,
  currentUser,
  preview,
  onSubmissionUpdated,
}: {
  definition: FormDefinition
  submission: FormSubmission
  /** `id` is the local User id — what "only the assignee may sign" compares. */
  currentUser: { id?: string; name: string }
  preview?: boolean
  onSubmissionUpdated: (updated: FormSubmission) => void
}) {
  const reviewFields = allFields(definition).filter(f => f.fieldType === 'review')
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  const reviews = useQuery({
    queryKey: ['form-reviews', tenantId, submission.id, submission.status],
    queryFn: () => formsApi.listReviews(submission.id),
    enabled: !preview && reviewFields.length > 0 && !!tenantId,
  })

  if (reviewFields.length === 0) return null

  // The builder preview renders against a submission that was never saved, so
  // it shows the area's shape with nothing to assign or sign.
  const rows: ReviewView[] = preview
    ? reviewFields.map(f => ({
        fieldId: f.id,
        label: f.label,
        helpText: f.helpText ?? null,
        status: 'unassigned',
        assignee: null,
        assignedBy: null,
        assignedAt: null,
        signedBy: null,
        signedAt: null,
        executable: false,
      }))
    : (reviews.data ?? [])

  return (
    <section
      aria-label='Review'
      // Light grey while the form is being filled; light green once it is
      // completed and ready for review (z8nrz7e2mu) — the colour says the
      // reviews can now be signed.
      className={cn(
        'space-y-2 rounded-xl border p-4',
        submission.status === 'pending_review'
          ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40'
          : 'border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60',
      )}
    >
      <h3 className='text-base font-semibold text-gray-900 dark:text-gray-100'>Review</h3>
      {reviews.isError && (
        <p className='text-sm text-destructive'>
          {apiErrorMessage(reviews.error, 'Could not load the reviews on this form.')}
        </p>
      )}
      {!preview && reviews.isLoading && <p className='text-sm text-muted-foreground'>Loading…</p>}
      {rows.map(r => (
        <ReviewRow
          key={r.fieldId}
          review={r}
          submission={submission}
          currentUser={currentUser}
          preview={preview}
          onSubmissionUpdated={onSubmissionUpdated}
        />
      ))}
    </section>
  )
}

function ReviewRow({
  review,
  submission,
  currentUser,
  preview,
  onSubmissionUpdated,
}: {
  review: ReviewView
  submission: FormSubmission
  currentUser: { id?: string; name: string }
  preview?: boolean
  onSubmissionUpdated: (updated: FormSubmission) => void
}) {
  const { can } = useOptionalPermission()
  const queryClient = useQueryClient()
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  const invalidateWorklist = useInvalidatePendingReviews()
  const invalidateAuditTrail = useAuditTrailInvalidator()
  const [assigning, setAssigning] = useState(false)
  const [signing, setSigning] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reviewed = review.status === 'reviewed'
  const formOpenOrWaiting =
    submission.status === 'draft' || submission.status === 'active' || submission.status === 'pending_review'
  const canAssign = !preview && !reviewed && formOpenOrWaiting && can(PERMISSIONS.submissionsEdit)

  const assignees = useQuery({
    queryKey: ['review-assignees', tenantId, submission.id, review.fieldId],
    queryFn: () => formsApi.listReviewAssignees(submission.id, review.fieldId),
    enabled: canAssign && !!tenantId,
    staleTime: 30_000,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['form-reviews'] })
    invalidateWorklist()
    invalidateAuditTrail()
  }

  const assign = async (value: string) => {
    const assigneeId = value === UNASSIGNED ? null : value
    if (assigneeId === (review.assignee?.id ?? null)) return
    setAssigning(true)
    setError(null)
    try {
      await formsApi.assignReview(submission.id, review.fieldId, assigneeId)
      refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'The reviewer could not be assigned.'))
    } finally {
      setAssigning(false)
    }
  }

  const sign = async (signingToken: string) => {
    if (!submission.completedAt) return
    setBusy(true)
    setError(null)
    try {
      const result = await formsApi.signReview(
        submission.id,
        review.fieldId,
        signingToken,
        submission.completedAt,
      )
      setSigning(false)
      refresh()
      // The last review completes the form: hand the host the new status.
      if (result.submissionStatus !== submission.status) {
        onSubmissionUpdated(await formsApi.getSubmission(submission.id))
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'The review could not be signed.'))
    } finally {
      setBusy(false)
    }
  }

  const isAssignee = !!currentUser.id && review.assignee?.id === currentUser.id
  const signBlocked = reviewed
    ? null
    : !review.executable
      ? 'Available once the form is completed'
      : !review.assignee
        ? 'Assign a reviewer first'
        : !isAssignee
          ? `Assigned to ${review.assignee.name} — only they can sign it`
          : !can(PERMISSIONS.submissionsReview)
            ? 'You do not have the Review submissions permission'
            : null

  // The current assignee stays selectable even when the list no longer offers
  // them, so the control never shows a blank for a real assignment.
  const options = assignees.data ?? []
  const withCurrent =
    review.assignee && !options.some(o => o.id === review.assignee?.id)
      ? [{ id: review.assignee.id, name: review.assignee.name }, ...options]
      : options

  return (
    <div className='space-y-1.5 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900'>
      <div className='text-sm font-medium'>{review.label}</div>
      {review.helpText && <p className='text-xs text-muted-foreground'>{review.helpText}</p>}
      <div className='flex flex-wrap items-end gap-4'>
        <div className='min-w-52 space-y-1'>
          <span className='text-xs text-muted-foreground'>Assigned</span>
          {canAssign ? (
            <Select
              value={review.assignee?.id ?? UNASSIGNED}
              onValueChange={v => void assign(v)}
              disabled={assigning}
            >
              <SelectTrigger className='h-8 text-sm' aria-label={`Reviewer for ${review.label}`}>
                <SelectValue placeholder='Select reviewer' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {withCurrent.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                    {o.id === currentUser.id ? ' (me)' : ''}
                  </SelectItem>
                ))}
                {assignees.isSuccess && options.length === 0 && (
                  <p className='px-2 py-1.5 text-xs text-muted-foreground'>
                    No one else holds the Review submissions permission.
                  </p>
                )}
              </SelectContent>
            </Select>
          ) : (
            <div className='text-sm'>{review.assignee?.name ?? '—'}</div>
          )}
        </div>

        <div className='space-y-1'>
          <span className='block text-xs text-muted-foreground'>Status</span>
          <span
            className={cn(
              'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
              STATUS_CHIP[review.status].className,
            )}
          >
            {STATUS_CHIP[review.status].label}
          </span>
        </div>

        <div className='ml-auto'>
          {reviewed ? (
            <span className='inline-flex items-center gap-1.5 text-sm text-green-800'>
              <ShieldCheck className='h-4 w-4' />
              Signed by <span className='font-medium'>{review.signedBy ?? '—'}</span> on {when(review.signedAt)}
            </span>
          ) : (
            <Button
              size='sm'
              variant='outline'
              disabled={!!signBlocked || preview}
              title={signBlocked ?? undefined}
              // Blue once it can actually be used (z8nrz7e2mu): the form is
              // completed and this is the assignee. Outlined, and disabled,
              // until then.
              className={cn(
                !signBlocked &&
                  !preview &&
                  'border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:text-white',
              )}
              onClick={() => {
                setError(null)
                setSigning(true)
              }}
            >
              <Lock className='mr-1.5 h-3.5 w-3.5' />
              Electronic Signature
            </Button>
          )}
        </div>
      </div>
      {!reviewed && signBlocked && !preview && (
        <p className='text-xs text-muted-foreground'>{signBlocked}.</p>
      )}
      {error && <p className='text-xs text-destructive'>{error}</p>}

      <SignatureDialog
        open={signing}
        onOpenChange={o => !busy && setSigning(o)}
        title={`Sign "${review.label}"`}
        description={
          <>
            <span className='block font-medium'>Meaning: Reviewed</span>
            <span className='block'>
              By signing you confirm you have reviewed this completed record and that it is
              accurate and complete.
            </span>
          </>
        }
        confirmLabel='Sign as Reviewed'
        signerName={currentUser.name}
        busy={busy}
        error={error}
        onConfirm={token => void sign(token)}
      />
    </div>
  )
}
